import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import { supabase } from "../supabaseClient";
import { ShieldLock } from "react-bootstrap-icons";

// Libellé du facteur et nom de l'entrée correspondante dans l'application d'authentification
// (issuer « Hopfone (nom) » pour les facteurs nommés, « Hopfone » pour les plus anciens).
const factorLabel = (factor, index) => factor?.friendly_name || `Facteur ${index + 1}`;
const authenticatorEntry = (factor) => factor?.friendly_name ? `Hopfone (${factor.friendly_name})` : "Hopfone";

// Gestion du second facteur (TOTP) de l'utilisateur connecté.
const MfaSettings = () => {
  const [factors, setFactors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingFactorId, setDeletingFactorId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  // Ajout d'un facteur : null (fermé), 'name' (choix du nom) puis 'verify' (QR code + saisie du code).
  const [addStage, setAddStage] = useState(null);
  const [friendlyName, setFriendlyName] = useState("");
  const [enrollData, setEnrollData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const refreshFactors = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      setFactors(data?.totp ?? []);
    } catch (error) {
      setErrorMsg(error?.message ?? "Erreur lors du chargement des facteurs MFA.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshFactors();
  }, [refreshFactors]);

  const onDeleteFactor = async (factor, index) => {
    const isLast = factors.length === 1;
    const message = `Supprimer le facteur « ${factorLabel(factor, index)} » ?`
      + (isLast ? "\n\nC'est votre dernier facteur : la double authentification sera désactivée." : "")
      + `\n\nPensez à supprimer aussi l'entrée « ${authenticatorEntry(factor)} » de votre application d'authentification.`;
    if (!window.confirm(message)) return;

    setDeletingFactorId(factor.id);
    setErrorMsg("");
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
      if (error) throw error;
      await refreshFactors();
    } catch (error) {
      setErrorMsg(error?.message ?? "Impossible de supprimer ce facteur MFA.");
    } finally {
      setDeletingFactorId(null);
    }
  };

  const onStartAdd = () => {
    setErrorMsg("");
    setFriendlyName(`Appareil ${factors.length + 1}`);
    setAddStage("name");
  };

  const onEnroll = async (e) => {
    e.preventDefault();
    const name = friendlyName.trim();
    if (factors.some((f) => f.friendly_name === name)) {
      setErrorMsg(`Un facteur nommé « ${name} » existe déjà.`);
      return;
    }
    setBusy(true);
    setErrorMsg("");
    try {
      // Nettoie les tentatives abandonnées (facteurs non vérifiés) avant d'en créer un nouveau.
      const { data: factorsData, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw listError;
      const unverified = (factorsData?.all ?? []).filter((f) => f.factor_type === "totp" && f.status === "unverified");
      for (const factor of unverified) {
        const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
        if (unenrollError) throw unenrollError;
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: name,
        // Même nommage que l'app mobile : l'entrée « Hopfone (nom) » identifie le facteur dans l'application d'authentification.
        issuer: `Hopfone (${name})`,
      });
      if (error) throw error;
      setEnrollData(data);
      setVerifyCode("");
      setCopied(false);
      setAddStage("verify");
    } catch (error) {
      setErrorMsg(error?.message ?? "Impossible d'ajouter un facteur MFA.");
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrorMsg("");
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: enrollData.id,
        code: verifyCode.trim(),
      });
      if (error) throw error;
      setAddStage(null);
      setEnrollData(null);
      await refreshFactors();
    } catch (error) {
      setErrorMsg(error?.message ?? "Code invalide.");
    } finally {
      setBusy(false);
    }
  };

  // Abandon : le facteur créé mais non vérifié est supprimé pour ne pas laisser de tentative orpheline.
  const onCancelAdd = async () => {
    if (enrollData?.id) {
      await supabase.auth.mfa.unenroll({ factorId: enrollData.id });
    }
    setAddStage(null);
    setEnrollData(null);
    setErrorMsg("");
  };

  const onCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(enrollData?.totp?.secret ?? "");
      setCopied(true);
    } catch (error) {
      setErrorMsg("Copie impossible, sélectionnez le code manuellement.");
    }
  };

  return (
    <div>
      <h5 className="account-card-title"><ShieldLock size={20} />Double authentification (MFA)</h5>
      <p className="text-muted">
        {factors.length > 0
          ? "La double authentification est activée. Un code de l'un de ces facteurs vous sera demandé à chaque connexion."
          : "La double authentification n'est pas activée."}
      </p>

      {loading ? (
        <Spinner animation="border" size="sm" />
      ) : factors.length > 0 && (
        <ListGroup className="mb-3">
          {factors.map((factor, index) => (
            <ListGroup.Item key={factor.id} className="d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-bold">{factorLabel(factor, index)}</div>
                <small className="text-muted">
                  Entrée « {authenticatorEntry(factor)} » · ajouté le {new Date(factor.created_at).toLocaleDateString("fr-FR")}
                </small>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                disabled={deletingFactorId === factor.id}
                onClick={() => onDeleteFactor(factor, index)}>
                {deletingFactorId === factor.id ? "Suppression..." : "Supprimer"}
              </Button>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {addStage === null && !loading && (
        <Button variant="primary" onClick={onStartAdd}>
          Ajouter un facteur
        </Button>
      )}

      {addStage === "name" && (
        <Form onSubmit={onEnroll} className="mb-3">
          <h6>Étape 1/2 : nommez ce facteur</h6>
          <Form.Group className="mb-2" controlId="mfaFriendlyName">
            <Form.Label>Nom du facteur</Form.Label>
            <Form.Control
              type="text"
              maxLength={30}
              placeholder="iPhone, 1Password..."
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
              autoFocus
              required
            />
            <Form.Text muted>
              Ce nom vous permettra de reconnaître ce facteur à la connexion et dans votre application d'authentification.
            </Form.Text>
          </Form.Group>
          <Button type="submit" disabled={busy || !friendlyName.trim()} className="me-2">
            {busy ? "Génération..." : "Générer le QR code"}
          </Button>
          <Button variant="link" onClick={onCancelAdd} disabled={busy}>
            Annuler
          </Button>
        </Form>
      )}

      {addStage === "verify" && enrollData && (
        <Form onSubmit={onVerify} className="mb-3">
          <h6>Étape 2/2 : enregistrez « {friendlyName.trim()} » dans votre application d'authentification</h6>
          <p className="mb-1">Flashez ce QR code avec votre application d'authentification :</p>
          <img src={enrollData.totp.qr_code} alt="QR code MFA" width={180} height={180} className="mb-2 bg-white p-2 border rounded" />
          <p className="mb-1">Ou saisissez ce code dans votre application d'authentification :</p>
          <div className="d-flex align-items-center mb-3">
            <code className="me-2 user-select-all">{enrollData.totp.secret}</code>
            <Button variant="outline-secondary" size="sm" onClick={onCopySecret}>
              {copied ? "Copié" : "Copier"}
            </Button>
          </div>
          <Form.Group className="mb-2" controlId="mfaVerifyCode">
            <Form.Label>
              Saisissez le code à 6 chiffres affiché pour l'entrée « Hopfone ({friendlyName.trim()}) »
            </Form.Label>
            <Form.Control
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              style={{ maxWidth: 160 }}
              required
            />
          </Form.Group>
          <Button type="submit" disabled={busy || !verifyCode.trim()} className="me-2">
            {busy ? "Vérification..." : "Vérifier et activer"}
          </Button>
          <Button variant="link" onClick={onCancelAdd} disabled={busy}>
            Annuler
          </Button>
        </Form>
      )}

      {errorMsg && (
        <Alert variant="danger" onClose={() => setErrorMsg("")} dismissible>
          {errorMsg}
        </Alert>
      )}
    </div>
  );
};

export default MfaSettings;
